/**
 * 通用 UI 组件（无框架，纯 DOM）
 * ------------------------------------------------------------
 * 只放「多个页面都要用」的小组件；业务渲染留在各自 pages/ 里。
 * 样式约定与 src/styles/components.css 保持一致。
 */
import { el, escapeHtml } from '../utils/index.js';

export { el };

/* ------------------------------ 基础块 ------------------------------ */

/** 空状态：<div class="empty"><span class="icon">…</span><p>…</p></div> */
export function emptyState(icon, title, desc = '', actionHtml = '') {
  return `<div class="empty">
    <span class="icon">${icon}</span>
    <p><strong>${escapeHtml(title)}</strong></p>
    ${desc ? `<p class="muted">${escapeHtml(desc)}</p>` : ''}
    ${actionHtml}
  </div>`;
}

/** 进度条 */
export function progressBar(percent, cls = '') {
  const p = Math.max(0, Math.min(100, Math.round(percent)));
  return `<div class="progress ${cls}"><i style="width:${p}%"></i></div>`;
}

/** 卡片 */
export function card(innerHtml, { title = '', extra = '', cls = '' } = {}) {
  return `<section class="card ${cls}">
    ${title ? `<div class="card-head"><h3>${escapeHtml(title)}</h3>${extra}</div>` : ''}
    ${innerHtml}
  </section>`;
}

/** 统计值（配合 .stat-row 使用） */
export function stat(label, value, unit = '') {
  return `<div class="stat">
    <div class="v">${escapeHtml(String(value))}${unit ? `<small>${escapeHtml(unit)}</small>` : ''}</div>
    <div class="k">${escapeHtml(label)}</div>
  </div>`;
}

export function statRow(items) {
  return `<div class="stat-row">${items.map(i => stat(i.label, i.value, i.unit)).join('')}</div>`;
}

/* ------------------------------ 模态框 ------------------------------ */

let modalCleanup = null;

/**
 * 打开模态框
 * @param {{title:string, body:string, footer?:string, size?:'slim'|'normal'|'wide', onMount?:(root:HTMLElement)=>void}} cfg
 *   size: slim → .modal.slim / wide → .modal.wide / 其余为默认
 */
export function openModal({ title, body, footer = '', size = 'normal', onMount }) {
  closeModal();
  const host = document.getElementById('modal-root');
  if (!host) return null;

  host.innerHTML = `
    <div class="modal-mask" data-close="1">
      <div class="modal ${size === 'wide' ? 'wide' : size === 'slim' ? 'slim' : ''}" role="dialog" aria-modal="true">
        <header>
          <h3>${escapeHtml(title)}</h3>
          <button class="icon-btn" data-close="1" type="button" aria-label="关闭">✕</button>
        </header>
        <div class="body">${body}</div>
        ${footer ? `<footer>${footer}</footer>` : ''}
      </div>
    </div>`;

  const mask = host.querySelector('.modal-mask');
  const onClick = e => { if (e.target?.dataset?.close === '1') closeModal(); };
  const onKey = e => { if (e.key === 'Escape') closeModal(); };
  mask.addEventListener('click', onClick);
  document.addEventListener('keydown', onKey);
  modalCleanup = () => {
    mask.removeEventListener('click', onClick);
    document.removeEventListener('keydown', onKey);
    host.innerHTML = '';
    modalCleanup = null;
  };

  const modal = host.querySelector('.modal');
  onMount?.(modal);
  return modal;
}

export function closeModal() {
  modalCleanup?.();
}

/** 确认框，返回 Promise<boolean> */
export function confirmDialog({ title = '确认', message = '', okText = '确定', cancelText = '取消', danger = false } = {}) {
  return new Promise(resolve => {
    const modal = openModal({
      title,
      size: 'slim',
      body: `<p class="modal-text">${escapeHtml(message)}</p>`,
      footer: `<button class="btn btn-ghost" data-act="cancel" type="button">${escapeHtml(cancelText)}</button>
               <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" data-act="ok" type="button">${escapeHtml(okText)}</button>`,
    });
    if (!modal) return resolve(false);
    modal.querySelector('[data-act="cancel"]').onclick = () => { closeModal(); resolve(false); };
    modal.querySelector('[data-act="ok"]').onclick = () => { closeModal(); resolve(true); };
  });
}

/* ------------------------------ 图表 ------------------------------ */

let chartSeq = 0;

/**
 * 折线图（成绩进步曲线）
 * @param {Array<{label:string, value:number}>} points
 */
export function lineChart(points, { height = 170, max = 100 } = {}) {
  if (!points.length) {
    return emptyState('📈', '还没有成绩记录', '完成一次练习后，这里会出现你的进步曲线');
  }
  const id = `lc${++chartSeq}`;
  const W = 560, H = height, padL = 32, padR = 14, padT = 14, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = points.length;
  const x = i => padL + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = v => padT + innerH - (Math.max(0, Math.min(max, v)) / max) * innerH;

  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${path} L${x(n - 1).toFixed(1)},${(padT + innerH).toFixed(1)} L${x(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  const grid = [0, 25, 50, 75, 100].map(v => `
    <line x1="${padL}" y1="${y(v).toFixed(1)}" x2="${W - padR}" y2="${y(v).toFixed(1)}" class="lc-grid"/>
    <text x="${padL - 6}" y="${(y(v) + 4).toFixed(1)}" class="lc-axis" text-anchor="end">${v}</text>`).join('');

  const dots = points.map((p, i) => `
    <circle cx="${x(i).toFixed(1)}" cy="${y(p.value).toFixed(1)}" r="4.5" class="lc-dot">
      <title>${escapeHtml(p.label)}：${p.value} 分</title>
    </circle>`).join('');

  return `<svg class="line-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="成绩曲线">
    <defs>
      <linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="var(--c-primary)" stop-opacity=".26"/>
        <stop offset="100%" stop-color="var(--c-primary)" stop-opacity="0"/>
      </linearGradient>
    </defs>
    ${grid}
    <path d="${area}" fill="url(#${id})"/>
    <path d="${path}" fill="none" class="lc-line"/>
    ${dots}
  </svg>`;
}

/** 水平条形图（模块正确率对比） */
export function barChart(items) {
  if (!items.length) return '';
  const max = Math.max(...items.map(i => i.value), 1);
  return `<div class="bar-chart">${items.map(i => `
    <div class="bar-row">
      <span class="bar-label">${escapeHtml(i.label)}</span>
      <span class="bar-track"><i style="width:${Math.round((i.value / max) * 100)}%" class="${i.cls || ''}"></i></span>
      <span class="bar-val">${i.value}${i.unit || '%'}</span>
    </div>`).join('')}</div>`;
}

/* ------------------------------ 掌握矩阵 ------------------------------ */

/**
 * 表格式掌握矩阵：行 = 知识点，列 = 级别
 * @param {Array<{level:number,name:string,icon?:string}>} levels
 * @param {Array<{label:string, sub?:string, levels:Object}>} rows
 */
export function masteryMatrix(levels, rows, { emptyText = '这个单元还没有练习记录。' } = {}) {
  if (!rows.length) return `<p class="muted">${escapeHtml(emptyText)}</p>`;
  const head = levels.map(l =>
    `<th title="${escapeHtml(l.name)}"><span class="lv-ico">${l.icon || ''}</span><span class="lv-no">L${l.level}</span></th>`
  ).join('');
  const body = rows.map(r => {
    const cells = levels.map(l => {
      const v = r.levels?.[String(l.level)];
      const cls = v ? `m-${v}` : 'm-none';
      const txt = v === 'pass' ? '✓' : v === 'hint' ? '△' : v === 'fail' ? '×' : '·';
      return `<td class="mx ${cls}" title="L${l.level} ${escapeHtml(l.name)}：${v === 'pass' ? '独立通过' : v === 'hint' ? '提示后通过' : v === 'fail' ? '未通过' : '未练习'}">${txt}</td>`;
    }).join('');
    return `<tr><th class="mx-name"><div>${escapeHtml(r.label)}</div>${r.sub ? `<div class="muted" style="font-size:11px">${escapeHtml(r.sub)}</div>` : ''}</th>${cells}</tr>`;
  }).join('');

  return `<div class="matrix-wrap">
    <table class="matrix-table">
      <thead><tr><th class="mx-name">知识点</th>${head}</tr></thead>
      <tbody>${body}</tbody>
    </table>
    <div class="matrix-legend">
      <span><i class="mx m-pass">✓</i>独立通过</span>
      <span><i class="mx m-hint">△</i>提示后通过</span>
      <span><i class="mx m-fail">×</i>未通过</span>
      <span><i class="mx m-none">·</i>未练习</span>
    </div>
  </div>`;
}

/** 徽章墙 */
export function badgeWall(items) {
  return `<div class="badge-wall">${items.map(b => `
    <div class="badge ${b.unlocked ? 'on' : 'off'}" title="${escapeHtml(b.desc || '')}">
      <div class="badge-ico">${b.icon || '🏅'}</div>
      <div class="badge-name">${escapeHtml(b.name)}</div>
      <div class="badge-sub">${b.unlocked ? `已获得 +${b.xp || 0} XP` : '未解锁'}</div>
    </div>`).join('')}</div>`;
}

/** 小圆点标签 */
export function tag(text, cls = '') {
  return `<span class="tag ${cls}">${escapeHtml(text)}</span>`;
}
